import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftRight, Mail, CreditCard, CheckCircle2, CalendarClock, AlertTriangle, Archive, RefreshCw, Plus } from 'lucide-react';
import { TRANSFER_PIPELINE, type AdminTransferList, type TransferStatus } from '@platform/shared-types';
import { InventoryPage } from '../inventory/InventoryChrome';
import { businessTransferApi } from '../../services/businessTransferApi';
import { AGE_CHIP, OWNER_CHIP, OWNER_LABEL, STATUS_LABEL, ago } from './format';
import NewTransferModal from './panels/NewTransferModal';
import { useIsSuperAdmin } from './useIsSuperAdmin';

const ICONS: Record<string, { Icon: React.FC<any>; accent: string; tile: string }> = {
  buyer_onboarding: { Icon: Mail, accent: '#B45309', tile: 'bg-amber-100 text-amber-700' },
  card_processing: { Icon: CreditCard, accent: '#9F1239', tile: 'bg-rose-100 text-rose-700' },
  ready_to_run: { Icon: CheckCircle2, accent: '#047857', tile: 'bg-emerald-100 text-emerald-700' },
  scheduled: { Icon: CalendarClock, accent: '#0E7490', tile: 'bg-cyan-100 text-cyan-700' },
  awaiting_merchant_link: { Icon: AlertTriangle, accent: '#C2410C', tile: 'bg-orange-100 text-orange-700' },
  completed: { Icon: Archive, accent: '#44403C', tile: 'bg-stone-100 text-stone-700' },
};
const CARD_CHIP = { seller_live: ['Seller’s, until run', 'bg-stone-100 text-stone-600'], pending_link: ['Paused', 'bg-amber-100 text-amber-800'], linked: ['Linked', 'bg-emerald-100 text-emerald-800'] } as const;

export default function BusinessTransfersPage() {
  const navigate = useNavigate();
  const canAct = useIsSuperAdmin();
  const [data, setData] = useState<AdminTransferList | null>(null);
  const [error, setError] = useState('');
  const [stage, setStage] = useState<TransferStatus | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try { setData(await businessTransferApi.list()); } catch (e: any) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => (data?.items || []).filter((t) => (stage ? t.status === stage : t.status !== 'cancelled')), [data, stage]);
  const attention = (data?.items || []).filter((t) => t.status === 'awaiting_merchant_link' || t.status === 'failed');

  return (
    <InventoryPage icon={ArrowLeftRight} title="Business transfers" subtitle="Store sales and changes of owner, tracked like orders" crumb="Operations"
      actions={<div className="flex gap-2">
        <button type="button" onClick={load} className="flex min-h-[44px] items-center gap-2 rounded-lg border border-white/10 px-3 text-sm text-white"><RefreshCw size={16} />Refresh</button>
        {canAct && <button type="button" onClick={() => setCreating(true)} className="flex min-h-[44px] items-center gap-2 rounded-lg bg-[#DC143C] px-4 text-sm font-semibold text-white"><Plus size={16} />New transfer</button>}
      </div>}>
      {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="mb-3 grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-6">
        {TRANSFER_PIPELINE.map(({ status, label }) => {
          const { Icon, accent, tile } = ICONS[status];
          const active = stage === status;
          return (
            <button key={status} type="button" aria-pressed={active} onClick={() => setStage(active ? null : status)}
              className="flex min-h-[56px] items-center gap-3 rounded-xl border border-stone-200 bg-white px-3 text-left"
              style={active ? { boxShadow: `inset 0 -3px 0 ${accent}` } : undefined}>
              <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${tile}`}><Icon size={16} /></span>
              <span><b className="block font-mono text-lg leading-none" style={{ color: accent }}>{data?.counts?.[status] ?? 0}</b><span className="text-[11px] text-stone-600">{label}</span></span>
            </button>
          );
        })}
      </div>

      {attention.length > 0 && !stage && (
        <div className="mb-3 rounded-xl border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-900">
          {attention.length} transfer{attention.length === 1 ? '' : 's'} need attention — card payments paused or a run failed.
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-stone-50">
            <tr>{['Transfer', 'Type', 'From → To', 'Now at', 'Waiting on', 'Age', 'Card payments'].map((h) => <th key={h} className="whitespace-nowrap px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-stone-500">{h}</th>)}</tr>
          </thead>
          <tbody>
            {visible.map((t) => (
              <tr key={t.transferId} onClick={() => navigate(`/business-transfers/${t.transferId}`)} className="cursor-pointer border-t border-stone-100 hover:bg-rose-50/40">
                <td className="px-3 py-3 font-mono font-semibold">{t.ref}</td>
                <td className="px-3 py-3"><span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${t.mode === 'store' ? 'bg-rose-50 text-rose-800' : 'border border-stone-200 bg-stone-50 text-stone-700'}`}>{t.mode === 'store' ? 'One store' : 'Whole business'}</span></td>
                <td className="px-3 py-3">{t.sellerName}<div className="text-xs text-stone-500">{t.locationName ? `${t.locationName} → ` : '→ '}{t.buyerName}</div></td>
                <td className="px-3 py-3">{t.nowAt || STATUS_LABEL[t.status]}</td>
                <td className="px-3 py-3">{t.waitingOn && <span className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ${OWNER_CHIP[t.waitingOn]}`}>{OWNER_LABEL[t.waitingOn]}</span>}</td>
                <td className="px-3 py-3">{t.age && <span className={`rounded-full px-2 py-0.5 font-mono text-[11px] font-semibold ${AGE_CHIP[t.age]}`}>{ago(t.updatedAt)}</span>}</td>
                <td className="px-3 py-3"><span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${CARD_CHIP[t.cardPayments][1]}`}>{CARD_CHIP[t.cardPayments][0]}{t.cardPayments === 'pending_link' ? ` · ${t.linksDone}/${t.linksTotal}` : ''}</span></td>
              </tr>
            ))}
            {!visible.length && <tr><td colSpan={7} className="px-3 py-10 text-center text-stone-500">{stage ? `No transfers are ${STATUS_LABEL[stage].toLowerCase()} right now.` : 'No transfers yet.'}</td></tr>}
          </tbody>
        </table>
      </div>
      {creating && <NewTransferModal onClose={() => setCreating(false)} onCreated={(id: string) => navigate(`/business-transfers/${id}`)} />}
    </InventoryPage>
  );
}
